/* Workbench API client (W6). Two reads: the counts behind the four
 * surface lines, and the existing /api/tasks queue narrowed per surface
 * by the placement oracle (no parallel task endpoint). */

import { apiFetch } from '../../lib/api'
import {
  DEFERRED_TASK_TYPES,
  isDeferredTaskType,
  type TaskQueueItem,
  type TaskQueueResponse,
} from '../../lib/tasks'
import { modeOf, placementFor, surfaceFor, type Surface } from './placement'

export interface SubStateCount {
  key: string
  label: string
  count: number
}

export interface AdminKindCount {
  key: string
  label: string
  count: number
  /** The one verb the officer keeps on this kind ("retry"), or null
   *  when the kind is admin-only (`unrouted`). */
  officer_verb: string | null
}

export interface SurfaceLine {
  key: Surface
  name: string
  total: number
  sub_states: SubStateCount[]
  oldest_at: string | null
  oldest_age_days: number | null
  /** The task type of the oldest open item, when the counts carry it
   *  (the landing reads "overdue" from `request_overdue`). The backend
   *  does not send it yet (2026-09-03) — absent, the age reads "oldest". */
  oldest_task_type?: string | null
  /** System failures on this line — never an officer queue row, never
   *  in `total`, but always visible. */
  admin_total: number
  admin_kinds: AdminKindCount[]
  admin_oldest_age_days: number | null
}

export interface WalkStatusLine {
  task_type: string
  status_line: string
  count: number
}

export interface FundChip {
  fund_id: string
  name: string
  total: number
}

export interface WorkbenchCounts {
  surfaces: SurfaceLine[]
  funds: FundChip[]
  walk_status: WalkStatusLine[]
  last_document_processed_at: string | null
  generated_at: string
}

/* `background`: a heartbeat re-fetch, not the officer — never moves the
 * idle clock (lib/api.ts). */
export function fetchWorkbenchCounts(
  fundId?: string | null,
  background = false
): Promise<WorkbenchCounts> {
  const qs = fundId ? `?fund_id=${encodeURIComponent(fundId)}` : ''
  return apiFetch<WorkbenchCounts>(`/api/workbench/counts${qs}`, { background })
}

/** Every open task, fund-scoped. The per-surface split happens on the
 *  placement oracle so the workbench and the oracle can never disagree
 *  about where a row belongs. */
export async function fetchOpenTasks(
  fundId?: string | null,
  background = false
): Promise<TaskQueueItem[]> {
  const qs = fundId ? `&fund_id=${encodeURIComponent(fundId)}` : ''
  const res = await apiFetch<TaskQueueResponse>(`/api/tasks?status=open${qs}`, { background })
  return res.items ?? []
}

/** The queue rows for ONE surface — status placements are never rows,
 *  and neither are the DEFERRED task types (lib/tasks.ts): the daily-digest
 *  acknowledgement rows keep minting and closing server-side but left the
 *  officer's queue on 2026-09-04 (audit cut 1, UI removal only — the
 *  placement oracle still places them, so nothing is unmapped). */
export function tasksForSurface(tasks: TaskQueueItem[], surface: Surface): TaskQueueItem[] {
  return tasks.filter(
    (t) =>
      modeOf(t.task_type) === 'queue' &&
      surfaceFor(t.task_type) === surface &&
      !isDeferredTaskType(t.task_type)
  )
}

/** The sub-state cells the deferred task types are counted under — the
 *  counts endpoint keeps counting them; the landing tile does not. */
export const HIDDEN_SUB_STATES: ReadonlySet<string> = new Set(
  DEFERRED_TASK_TYPES.map((t) => placementFor(t).subState).filter((s): s is string => s !== null)
)

/** A queue tile's count with the hidden cells taken out — never below 0. */
export function visibleTotal(line: SurfaceLine): number {
  const hidden = line.sub_states
    .filter((s) => HIDDEN_SUB_STATES.has(s.key))
    .reduce((n, s) => n + s.count, 0)
  return Math.max(0, line.total - hidden)
}

/** The walk view's status lines — Record Keeping only, never counted as
 *  work (chain_orphan / look_through_incomplete). */
export function statusTasks(tasks: TaskQueueItem[]): TaskQueueItem[] {
  return tasks.filter((t) => modeOf(t.task_type) === 'status')
}

/** The system-failure rows behind the OTHER OPS admin segment. Never
 *  officer queue rows — they render in their own block, with the one
 *  verb the officer keeps. */
export function adminTasks(tasks: TaskQueueItem[]): TaskQueueItem[] {
  return tasks.filter((t) => modeOf(t.task_type) === 'admin')
}

export interface StuckActionResult {
  task_id: string
  outcome: string
  failure_reason?: string | null
  resolution_question?: string | null
}

/** Whole-group retry — the officer's one verb on a system failure.
 *  Dispatches to the EXISTING stuck-action endpoint, whose response is
 *  already honest about a re-dead-letter (WI-9). */
export async function retryFailure(taskId: string): Promise<StuckActionResult> {
  return apiFetch<StuckActionResult>(`/api/tasks/${taskId}/stuck-action`, {
    method: 'POST',
    body: JSON.stringify({ action: 'retry' }),
  })
}

/** Honest per-outcome wording — a retry that dead-letters again SAYS SO
 *  on the row; there is no silent requeue. */
export function retryOutcomeText(r: StuckActionResult): string {
  switch (r.outcome) {
    case 'requeued':
      return 'Requeued — the worker is replaying it.'
    case 'succeeded':
      return 'Landed.'
    case 'partial':
      return 'Landed, with a severed leg.'
    case 'dead_lettered_again':
      return `Failed again — ${r.failure_reason ?? 'the write did not land'}. Admin has to take this one.`
    case 'resolution_question':
      return `Back in review — ${r.resolution_question ?? 'a merge question was raised'}.`
    default:
      return r.outcome
  }
}
